import { Box, IconButton, Typography } from '@mui/material';
import React, { useEffect, useState, useMemo } from 'react';
import { HeaderBox } from './mBookListPage';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import GeetGovindServices from "../../Services/GeetGovind";
import KalyanServices from "../../Services/Kalyan";
import KalpataruServices from "../../Services/Kalpataru";
import BookDetailsCard from './Common/BookDetailsCard';
import { Product } from './MobileMagazines';

/**
 * Mobile magazine home — Geet Govind hub/detail, or Kalyan / Kalpataru issue detail.
 * Kalyan/Kalpataru entered from mMagazineListPage (or deep-link via App Navigation).
 */
const MMagazineHomePage = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const location: any = useLocation();
    const { lang: urlLang } = useParams();

    const [magazinesDetails, setMagazinesDetails] = useState<any>(null);
    const [geetGovindDetails, setGeetGovindDetails] = useState<any>();
    const [kalyanDetails, setKalyanDetails] = useState<any>(null);
    const [isLiked, setIsLiked] = useState<boolean>(false);
    const [magazineId, setMagazineId] = useState<any>();
    const [geetGovindBookId, setGeetGovindBookId] = useState<any>();
    const [kalyanId, setKalyanId] = useState<any>();
    const [totalRecords, setTotalRecords] = useState<any>();
    const [magazineToc, setMagazineToc] = useState<any[]>([]);
    const [magazineDescription, setMagazineDescription] = useState<string>('');

    const pathname = location?.pathname || '';
    const isKalpataruPath = pathname.includes('/magazines/kalyanakalpataru');
    const isKalyanPath = pathname.includes('/magazines/kalyan') && !isKalpataruPath;
    const isKalpataruBook = !!location?.state?.isKalpataruBook || !!location?.state?.kalpataruId || isKalpataruPath;
    const kalyanIssueId =
        location?.state?.kalyansId ||
        location?.state?.kalpataruId ||
        location?.state?.kalyanId ||
        location?.state?.magzineId ||
        '';
    const isKalyanContext = !!(kalyanIssueId || isKalyanPath || isKalpataruPath) &&
        !location?.state?.geetGovindId &&
        location?.state?.productType !== Product.MonthlyMagazine &&
        !pathname.includes('/magazines/geetgovind');

    // geetGovindId = real monthly issue id (carousel); hub may arrive with lang only
    const detailMagazineId = location?.state?.geetGovindId;
    const incomingMagazineId = location?.state?.geetGovindId || location?.state?.magzineId;
    const magazineLanguage =
        location?.state?.magazineLanguage ||
        location?.state?.magazineLangauge ||
        location?.state?.geetGovindLang ||
        (urlLang === 'hindi' || urlLang === 'english' ? urlLang : '') ||
        '';

    // Category list context — used by carousel "View All" → mMagazineListPage
    const magazineCategoryId =
        location?.state?.categoryId ||
        location?.state?.catid ||
        '';
    const magazineCategoryName =
        location?.state?.kalyanName ||
        location?.state?.kalpataruName ||
        '';
    const magazineCategoryLanguage =
        location?.state?.kalyansLanguage ||
        location?.state?.magazineLanguage ||
        '';

    const isSimpleLangLabel = (rawLang: any): boolean => {
        const lang = String(rawLang || '').toLowerCase();
        return lang === 'english' || lang === 'en' || lang === 'hindi' || lang === 'hi';
    };

    const inferGeetGovindLangLabel = (rawLang: any): string => {
        if (!rawLang && rawLang !== 0) return '';
        const lang = String(rawLang).toLowerCase();
        if (lang === 'english' || lang === 'en' || lang.includes('english') || lang.includes('angrezi')) return 'english';
        if (lang === 'hindi' || lang === 'hi' || lang.includes('hindi') || lang.includes('hin') || lang.includes('हिं') || lang.includes('हिन')) return 'hindi';
        return '';
    };

    const resolvedGeetGovindLangLabel = useMemo(() => {
        const fromState = inferGeetGovindLangLabel(
            location?.state?.geetGovindLang ||
            location?.state?.magazineLanguage ||
            location?.state?.magazineLangauge ||
            urlLang
        );
        if (fromState) return fromState;

        const fromDetails = inferGeetGovindLangLabel(
            geetGovindDetails?.monthlyMagazineLanguage ||
            magazinesDetails?.[0]?.monthlyMagazineLanguage ||
            magazinesDetails?.[0]?.magazineLanguage
        );
        if (fromDetails) return fromDetails;

        return 'hindi';
    }, [location?.state, urlLang, geetGovindDetails, magazinesDetails]);

    const resolvedMagazineLanguageId = useMemo(() => {
        const fromState = location?.state?.magazineLanguage || location?.state?.magazineLangauge || '';
        if (fromState && !isSimpleLangLabel(fromState)) return fromState;

        return geetGovindDetails?.monthlyMagazineLanguageId ||
            magazinesDetails?.[0]?.monthlyMagazineLanguageId ||
            magazinesDetails?.find((item: any) => item?.monthlyMagazineLanguageId)?.monthlyMagazineLanguageId ||
            (isSimpleLangLabel(fromState) ? fromState : '');
    }, [location?.state, geetGovindDetails, magazinesDetails]);

    // ---------- Kalyan / Kalpataru detail ----------
    useEffect(() => {
        if (!isKalyanContext || !kalyanIssueId) return;

        const logdetails: string[] = [
            `load_kalyan_home id=${kalyanIssueId}`,
            `isKalpataru=${isKalpataruBook}`,
        ];

        const load = isKalpataruBook
            ? KalpataruServices.getcurrentKalpatarul(String(kalyanIssueId))
            : KalyanServices.getcurrentKalyan(String(kalyanIssueId));

        load.then((res: any) => {
            if (res?.status && res?.result) {
                setKalyanDetails(res.result);
                setIsLiked(!!res.result?.isFavourite);
                setKalyanId(kalyanIssueId);
                if (res.result.description) {
                    setMagazineDescription(res.result.description);
                    logdetails.push('description=from_detail_api');
                }
                logdetails.push('kalyan_detail_success');
            } else {
                logdetails.push(`kalyan_detail_failed message=${res?.message}`);
                console.error('Kalyan/Kalpataru detail failed:', res?.message);
            }
            console.log('[mMagazineHomePage]', logdetails.join(' | '));
        }).catch((error: any) => {
            logdetails.push(`kalyan_detail_error=${error}`);
            console.error('Error loading Kalyan/Kalpataru detail:', error);
            console.log('[mMagazineHomePage]', logdetails.join(' | '));
        });
    }, [isKalyanContext, kalyanIssueId, isKalpataruBook]);

    // TOC for Kalyan / Kalpataru
    useEffect(() => {
        if (!isKalyanContext || !kalyanIssueId) return;

        const logdetails: string[] = [`load_kalyan_toc id=${kalyanIssueId}`];
        const navPromise = isKalpataruBook
            ? KalpataruServices.getNavigationDetails(String(kalyanIssueId))
            : KalyanServices.getNavigationDetails(String(kalyanIssueId));

        navPromise.then((navRes: any) => {
            if (navRes?.status && navRes?.result?.navigationTree) {
                setMagazineToc(navRes.result.navigationTree);
                logdetails.push(`toc_nodes=${navRes.result.navigationTree.length}`);
            } else {
                setMagazineToc([]);
                logdetails.push(`toc_failed message=${navRes?.message}`);
            }
            console.log('[mMagazineHomePage]', logdetails.join(' | '));
        }).catch((error: any) => {
            setMagazineToc([]);
            logdetails.push(`toc_error=${error}`);
            console.error('Error fetching Kalyan/Kalpataru TOC:', error);
            console.log('[mMagazineHomePage]', logdetails.join(' | '));
        });
    }, [isKalyanContext, kalyanIssueId, isKalpataruBook]);

    // Previous editions carousel — same related API as desktop Kalyan/Kalpataru detail
    useEffect(() => {
        if (!isKalyanContext || !kalyanIssueId) return;

        const logdetails: string[] = [
            `load_kalyan_related id=${kalyanIssueId}`,
            `isKalpataru=${isKalpataruBook}`,
        ];
        const relatedPromise = isKalpataruBook
            ? KalpataruServices.getRelatedKalpatrau(String(kalyanIssueId))
            : KalyanServices.getRelatedKalyans(String(kalyanIssueId));

        relatedPromise.then((res: any) => {
            if (res?.status && Array.isArray(res?.result)) {
                setMagazinesDetails(res.result);
                setTotalRecords(res.result.length);
                const sampleName = res.result[0]?.name || res.result[0]?.title || '';
                logdetails.push(`related_count=${res.result.length}`);
                logdetails.push(`sample_name=${sampleName || '(empty)'}`);
            } else if (res?.status && Array.isArray(res?.result?.items)) {
                setMagazinesDetails(res.result.items);
                setTotalRecords(res.result.totalRecords || res.result.items.length);
                const sampleName = res.result.items[0]?.name || res.result.items[0]?.title || '';
                logdetails.push(`related_items=${res.result.items.length}`);
                logdetails.push(`sample_name=${sampleName || '(empty)'}`);
            } else {
                setMagazinesDetails([]);
                setTotalRecords(0);
                logdetails.push(`related_failed message=${res?.message}`);
            }
            console.log('[mMagazineHomePage]', logdetails.join(' | '));
        }).catch((error: any) => {
            setMagazinesDetails([]);
            setTotalRecords(0);
            logdetails.push(`related_error=${error}`);
            console.error('Error fetching Kalyan/Kalpataru related editions:', error);
            console.log('[mMagazineHomePage]', logdetails.join(' | '));
        });
    }, [isKalyanContext, kalyanIssueId, isKalpataruBook]);

    // ---------- Geet Govind (existing) ----------
    useEffect(() => {
        if (isKalyanContext || !detailMagazineId) return;

        const logdetails: string[] = [`load_top_magazine id=${detailMagazineId}`];
        setMagazineId(detailMagazineId);
        setGeetGovindBookId(detailMagazineId);

        GeetGovindServices.getcurrentGeetGovind(String(detailMagazineId)).then((res: any) => {
            if (res?.status && res?.result) {
                setGeetGovindDetails(res.result);
                setIsLiked(res?.result?.isFavourite);
                if (res.result.description) {
                    setMagazineDescription(res.result.description);
                    logdetails.push('description=from_detail_api');
                }
                logdetails.push('getcurrentGeetGovind_success');
            } else {
                logdetails.push(`getcurrentGeetGovind_failed message=${res?.message}`);
                console.error("getcurrentGeetGovind failed:", res?.message);
            }
            console.log('[mMagazineHomePage]', logdetails.join(' | '));
        }).catch((error: any) => {
            logdetails.push(`getcurrentGeetGovind_error=${error}`);
            console.error("Error in getcurrentGeetGovind:", error);
            console.log('[mMagazineHomePage]', logdetails.join(' | '));
        });
    }, [detailMagazineId, isKalyanContext]);

    // Load monthly list for carousel (and fall back to first item when no id)
    useEffect(() => {
        if (isKalyanContext) return;

        const logdetails: string[] = [];
        const commonParams = {
            start: 0,
            length: 67,
            categoryId: "",
            IsChildLiterature: false,
            sort: "",
            authorId: "",
            search: "",
            isSpecial: false,
        };

        const listLang = magazineLanguage || 'hindi';
        logdetails.push(`load_monthly_list lang=${listLang}`);
        GeetGovindServices.getMonthlyMagazine(
            commonParams.search,
            commonParams.start,
            commonParams.length,
            commonParams.categoryId,
            commonParams.IsChildLiterature,
            commonParams.sort,
            commonParams.authorId,
            listLang,
            commonParams.isSpecial,
            "", ""
        ).then((res) => {
            if (res.status) {
                const items = res?.result?.items || [];
                setMagazinesDetails(items);
                setTotalRecords(res?.result?.totalRecords);

                const matched = incomingMagazineId
                    ? items.find((item: any) => String(item?.id) === String(incomingMagazineId))
                    : null;
                const resolvedId = matched?.id || items?.[0]?.id;
                if (resolvedId) {
                    setMagazineId(resolvedId);
                    if (matched) {
                        setGeetGovindDetails((prev: any) =>
                            (prev && String(prev.id) === String(matched.id)) ? prev : matched
                        );
                    } else if (!incomingMagazineId && items[0]) {
                        setGeetGovindDetails((prev: any) => prev || items[0]);
                    }
                }
                logdetails.push(incomingMagazineId
                    ? (matched ? `selected_from_param id=${resolvedId}` : `incoming_not_in_list fallback_first id=${resolvedId}`)
                    : `fallback_first id=${resolvedId}`);
            } else {
                logdetails.push(`list_failed message=${res.message}`);
                console.error("API call failed, status is false:", res.message);
            }
            console.log('[mMagazineHomePage]', logdetails.join(' | '));
        }).catch(error => {
            logdetails.push(`list_error=${error}`);
            console.error("Error fetching magazine details:", error);
            console.log('[mMagazineHomePage]', logdetails.join(' | '));
        });
    }, [magazineLanguage, incomingMagazineId, isKalyanContext]);

    // Favourite flag refresh when magazineId changes (Geet Govind only)
    useEffect(() => {
        if (isKalyanContext || !magazineId) return;

        const logdetails: string[] = [`refresh_favourite id=${magazineId}`];
        GeetGovindServices.getcurrentGeetGovind(String(magazineId)).then((res: any) => {
            if (res?.status) {
                setIsLiked(res?.result?.isFavourite);
                if (res?.result) {
                    setGeetGovindDetails(res.result);
                    if (res.result.description) {
                        setMagazineDescription(res.result.description);
                        logdetails.push('description=from_detail_api');
                    }
                }
                logdetails.push('refresh_favourite_success');
            } else {
                logdetails.push(`refresh_favourite_failed message=${res?.message}`);
            }
            console.log('[mMagazineHomePage]', logdetails.join(' | '));
        }).catch((error: any) => {
            logdetails.push(`refresh_favourite_error=${error}`);
            console.error("Error refreshing magazine favourite:", error);
            console.log('[mMagazineHomePage]', logdetails.join(' | '));
        });
    }, [magazineId, isKalyanContext]);

    // TOC via same navigation API as desktop GeetGovindDetailPage
    useEffect(() => {
        if (isKalyanContext || !magazineId) return;

        const logdetails: string[] = [`load_magazine_toc id=${magazineId}`];
        GeetGovindServices.getNavigationDetails(String(magazineId)).then((navRes: any) => {
            if (navRes?.status && navRes?.result?.navigationTree) {
                setMagazineToc(navRes.result.navigationTree);
                logdetails.push(`toc_nodes=${navRes.result.navigationTree.length}`);
            } else {
                setMagazineToc([]);
                logdetails.push(`toc_failed message=${navRes?.message}`);
            }
            console.log('[mMagazineHomePage]', logdetails.join(' | '));
        }).catch((error: any) => {
            setMagazineToc([]);
            logdetails.push(`toc_error=${error}`);
            console.error("Error fetching magazine TOC:", error);
            console.log('[mMagazineHomePage]', logdetails.join(' | '));
        });
    }, [magazineId, isKalyanContext]);

    const toggleLike = () => {
        const logdetails: string[] = [`toggle_like currentlyLiked=${isLiked}`];

        if (isKalyanContext) {
            const favId = kalyanId || kalyanIssueId;
            logdetails.push(`type=${isKalpataruBook ? 'kalpataru' : 'kalyan'} id=${favId}`);
            if (!favId) {
                logdetails.push('toggle_like_skipped no_id');
                console.log('[mMagazineHomePage]', logdetails.join(' | '));
                return;
            }
            if (!isLiked) {
                const add = isKalpataruBook
                    ? KalpataruServices.addKalpatruFavourite(favId)
                    : KalyanServices.addKalyanFavourite(favId);
                add.then((res: any) => {
                    if (res.status) setIsLiked(true);
                    logdetails.push(res.status ? 'add_favourite_success' : `add_favourite_failed message=${res?.message}`);
                    console.log('[mMagazineHomePage]', logdetails.join(' | '));
                });
            } else {
                const remove = isKalpataruBook
                    ? KalpataruServices.removeKalpatruFavourite(favId)
                    : KalyanServices.removeKalyanFavourite(favId);
                remove.then((res: any) => {
                    if (res.status) setIsLiked(false);
                    logdetails.push(res.status ? 'remove_favourite_success' : `remove_favourite_failed message=${res?.message}`);
                    console.log('[mMagazineHomePage]', logdetails.join(' | '));
                });
            }
            return;
        }

        const favId = magazineId || geetGovindBookId;
        logdetails.push(`type=geetgovind id=${favId}`);
        if (!favId) {
            logdetails.push('toggle_like_skipped no_id');
            console.log('[mMagazineHomePage]', logdetails.join(' | '));
            return;
        }

        if (!isLiked) {
            GeetGovindServices.addMagzineFavourite(favId).then((res: any) => {
                if (res.status) setIsLiked(true);
                logdetails.push(res.status ? 'add_favourite_success' : `add_favourite_failed message=${res?.message}`);
                console.log('[mMagazineHomePage]', logdetails.join(' | '));
            });
        } else {
            GeetGovindServices.removeMagzineFavourite(favId).then((res: any) => {
                if (res.status) setIsLiked(false);
                logdetails.push(res.status ? 'remove_favourite_success' : `remove_favourite_failed message=${res?.message}`);
                console.log('[mMagazineHomePage]', logdetails.join(' | '));
            });
        }
    };

    const itemType = useMemo(() => {
        if (isKalyanContext) {
            return isKalpataruBook ? t('itemtype_kalpataru_tr') : t('itemtype_kalyan_tr');
        }
        return resolvedGeetGovindLangLabel === 'english'
            ? t('itemtype_geetgovind_english_tr')
            : t('itemtype_geetgovind_hindi_tr');
    }, [t, isKalyanContext, isKalpataruBook, resolvedGeetGovindLangLabel]);

    const headerTitleKey = useMemo(() => {
        if (isKalyanContext) {
            return isKalpataruBook ? 'Kalpataru_lang_tr' : 'All_Kalyans';
        }
        return resolvedGeetGovindLangLabel === 'english'
            ? 'itemtype_geetgovind_english_tr'
            : 'itemtype_geetgovind_hindi_tr';
    }, [isKalyanContext, isKalpataruBook, resolvedGeetGovindLangLabel]);

    const hasContent = isKalyanContext
        ? !!kalyanDetails
        : !!(magazinesDetails || geetGovindDetails);

    return (
        <>
            <Box sx={{
                backgroundColor: "#F0F0F0",
                height: "100vh",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
            }}>
                <HeaderBox>
                    <>
                        <IconButton onClick={() => navigate(-1)} sx={{ position: 'absolute', left: 16, top: 35, p: 0 }}>
                            <ArrowBackIosIcon sx={{ color: "white", fontSize: '18px' }} />
                        </IconButton>
                        <IconButton onClick={() => navigate("/")} sx={{ position: 'absolute', left: 48, top: 35, p: 0 }}>
                            <HomeOutlinedIcon sx={{ color: "white", fontSize: '24px' }} />
                        </IconButton>
                        <Typography variant="caption" sx={{ color: "#ffffffdd", opacity: 0.9, mb: 0.5 }}>
                            {t("shrihari_tr")}
                        </Typography>
                        <Typography variant="h6" sx={{ color: "#ffffffdd", opacity: 0.9, fontSize: 15 }}>
                            {t(headerTitleKey)}
                        </Typography>
                    </>
                </HeaderBox>
                {hasContent ? (
                    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                        {isKalyanContext ? (
                            <BookDetailsCard
                                kalyanDetails={kalyanDetails}
                                magazinesDetails={magazinesDetails}
                                selectedMagazineId={kalyanId || kalyanIssueId}
                                isLiked={isLiked}
                                onToggleLike={toggleLike}
                                totalRecords={totalRecords}
                                itemType={itemType}
                                description={magazineDescription || kalyanDetails?.description || ''}
                                toc={magazineToc}
                                magazineMode
                                magazineProductType={isKalpataruBook ? Product.KalyanKalpataru : Product.Kalyan}
                                magazineCategoryId={magazineCategoryId}
                                magazineCategoryName={magazineCategoryName}
                                magazineCategoryLanguage={magazineCategoryLanguage}
                            />
                        ) : (
                            <BookDetailsCard
                                magazinesDetails={magazinesDetails}
                                geetGovindDetails={geetGovindDetails}
                                selectedMagazineId={magazineId}
                                magazineLang={resolvedGeetGovindLangLabel}
                                magazineLanguageId={resolvedMagazineLanguageId}
                                isLiked={isLiked}
                                onToggleLike={toggleLike}
                                totalRecords={totalRecords}
                                itemType={itemType}
                                description={magazineDescription || geetGovindDetails?.description || ''}
                                toc={magazineToc}
                                magazineMode
                                magazineProductType={Product.MonthlyMagazine}
                            />
                        )}
                    </Box>
                ) : (
                    <Typography variant="body1" sx={{ textAlign: 'center', mt: 4 }}>
                        {t("Loading details...")}
                    </Typography>
                )}
            </Box>
        </>
    );
};

export default MMagazineHomePage;
